import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  UpdateLeadGroup,
  fetchLeadData,
  fetchGetLeadsInGroupData,
  creatAddLeadsToGroup,
} from "../Services/APIService";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";

interface GroupData {
  groupId: number;
  groupName: string;
  groupPurpose: string;
  isActive: boolean;
  tenantId: number;
}

interface UpdateGroupCardProps {
  groupData: GroupData | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface Leads {
  leadId: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface SelectOption {
  value: number;
  label: string;
  LeadsData: Leads;
}

interface FormData {
  groupId: number | "";
  groupName: string;
  groupPurpose: string;
  isActive: boolean;
}

interface ValidationErrors {
  listOfLeads?: string;
}

const requiredField = <span className="text-danger ms-1">*</span>;

const UpdateGroupCard: React.FC<UpdateGroupCardProps> = ({
  groupData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    groupId: "",
    groupName: "",
    groupPurpose: "",
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [LeadsOptions, setLeadsOptions] = useState<SelectOption[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<SelectOption[]>([]);
  const [initialSelectedLeads, setInitialSelectedLeads] = useState<
    SelectOption[]
  >([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [activeTab, setActiveTab] = useState("home");

  useEffect(() => {
    if (groupData) {
      setFormData({
        groupId: groupData.groupId,
        groupName: groupData.groupName,
        groupPurpose: groupData.groupPurpose,
        isActive:
          typeof groupData.isActive === "boolean"
            ? groupData.isActive
            : groupData.isActive === "true",
      });
    }
  }, [groupData]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingLeads(true);
      setSelectedLeads([]);
      setInitialSelectedLeads([]);
      setLeadsOptions([]);

      try {
        const [allLeads, allAssignLeadsToGroup] = await Promise.all([
          fetchLeadData(),
          fetchGetLeadsInGroupData(groupData?.groupId ?? 0),
        ]);

        const options = allLeads.map((leads) => ({
          value: leads.leadId,
          label: `${leads.firstName} ${leads.lastName} (${leads.email})`,
          LeadsData: leads,
        }));

        setLeadsOptions(options);

        if (allAssignLeadsToGroup && allAssignLeadsToGroup.length > 0) {
          const selected = options.filter((option) =>
            allAssignLeadsToGroup.some(
              (groupLead) => groupLead.leadId === option.value
            )
          );
          setSelectedLeads(selected);
          setInitialSelectedLeads(selected);
        } else {
          setSelectedLeads([]);
          setInitialSelectedLeads([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingLeads(false);
      }
    };

    if (groupData?.groupId) {
      fetchData();
    }
  }, [groupData?.groupId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedLeads([]);
      return;
    }
    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedLeads(selectedOptions);
    if (errors.listOfLeads) {
      setErrors({ ...errors, listOfLeads: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.groupId === "" || isNaN(Number(formData.groupId))) {
      setError("Please enter a valid Group ID");
      return;
    }
    if (!formData.groupName.trim()) {
      setError("Please enter a Group Name");
      return;
    }
    if (!formData.groupPurpose.trim()) {
      setError("Please enter a Group Purpose");
      return;
    }

    try {
      setIsLoading(true);
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");
      if (!userId) throw new Error("User ID not found in localStorage");
      if (!tenantId) throw new Error("Tenant ID not found in localStorage");

      const payload = {
        groupID: formData.groupId,
        groupName: formData.groupName.trim().replace(/\s+/g, " "),
        groupPurpose: formData.groupPurpose,
        isActive: formData.isActive,
        modifiedBy: parseInt(userId),
        tenantId: parseInt(tenantId),
      };

      const data = await UpdateLeadGroup(payload);
      if (data.isUpdated) {
        toast.success("Lead Group Updated Successfully!");
        onUpdateComplete();
      } else {
        throw new Error("Failed to update lead group");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;
    if (!selectedLeads || selectedLeads.length === 0) {
      newErrors.listOfLeads = "Please select at least one lead";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAssignmentData()) return;
    if (!groupData || !groupData.groupId) {
      toast.error("Group data not found");
      return;
    }

    try {
      setIsLoading(true);
      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");
      if (!tenantId || !userId)
        throw new Error("Required session data not found");

      const currentSelectedLeads = selectedLeads;
      const initialSelectedLeadsList = initialSelectedLeads;
      const leadsToRemove = initialSelectedLeadsList.filter(
        (initialLead) =>
          !currentSelectedLeads.some(
            (currentLead) => currentLead.value === initialLead.value
          )
      );
      const leadsToAdd = currentSelectedLeads;

      if (leadsToRemove.length > 0) {
        const deleteData = {
          groupId: groupData.groupId,
          leads: leadsToRemove.map((lead) => ({ leadId: lead.value })),
          isAdd: false,
          tenantId: parseInt(tenantId),
          createdBy: parseInt(userId),
        };
        const removeResult = await creatAddLeadsToGroup(deleteData);
        if (!removeResult.isUpdated)
          throw new Error(removeResult.message || "Failed to unassign leads");
      }

      if (leadsToAdd.length > 0) {
        const createData = {
          groupId: groupData.groupId,
          leads: leadsToAdd.map((lead) => ({ leadId: lead.value })),
          isAdd: true,
          tenantId: parseInt(tenantId),
          createdBy: parseInt(userId),
        };
        const addResult = await creatAddLeadsToGroup(createData);
        if (!addResult.isUpdated)
          throw new Error(addResult.message || "Failed to assign new leads");
      }

      if (leadsToAdd.length > 0 || leadsToRemove.length > 0) {
        toast.success("Leads assigned successfully");
        setInitialSelectedLeads([...currentSelectedLeads]);
        setActiveTab("home");
      } else {
        toast.error("No changes in lead assignments");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      groupId: "",
      groupName: "",
      groupPurpose: "",
      isActive: true,
    });
    setError(null);
    onCancel();
  };

  return (
    <div className="card">
      <ul
        className="nav nav-tabs tabHeaderPadding cardHeaderHegiht"
        id="myTab"
        role="tablist"
        style={{ borderBottom: "1px solid #84556b" }}
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht ${
              activeTab === "home" ? "active" : ""
            }`}
            id="home-tab"
            onClick={() => handleTabClick("home")}
            type="button"
            role="tab"
            aria-controls="home-tab-pane"
            aria-selected={activeTab === "home"}
          >
            Update Lead Group
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht ${
              activeTab === "contact" ? "active" : ""
            }`}
            id="contact-tab"
            onClick={() => handleTabClick("contact")}
            type="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected={activeTab === "contact"}
          >
            Assign Leads
          </button>
        </li>
      </ul>

      <div className="tab-content card-body" id="myTabContent">
        <div
          className={`tab-pane fade ${
            activeTab === "home" ? "show active" : ""
          }`}
          id="home-tab-pane"
          role="tabpanel"
          aria-labelledby="home-tab"
          tabIndex={0}
        >
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger text-danger" role="alert">
                {error}
              </div>
            )}
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">Group Id{requiredField}</label>
                <input
                  type="number"
                  name="groupId"
                  className="form-control form-control-lg"
                  placeholder="Enter Group Id"
                  value={formData.groupId}
                  onChange={handleChange}
                  disabled
                  required
                />
              </div>
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">
                  Lead Group Name{requiredField}
                </label>
                <input
                  type="text"
                  name="groupName"
                  className="form-control form-control-lg"
                  placeholder="Enter Group Name"
                  value={formData.groupName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">
                  Lead Group Purpose{requiredField}
                </label>
                <textarea
                  name="groupPurpose"
                  className="form-control form-control-lg"
                  placeholder="Enter Group Purpose"
                  value={formData.groupPurpose}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-12 col-lg-6 mt-4">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span className="form-check-label">Active</span>
                </label>
              </div>
            </div>
          </form>
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "contact" ? "show active" : ""
          }`}
          id="contact-tab-pane"
          role="tabpanel"
          aria-labelledby="contact-tab"
          tabIndex={0}
        >
          <form>
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">
                  Assign Leads{requiredField}
                </label>
                <Select
                  options={LeadsOptions}
                  value={selectedLeads}
                  onChange={handleMultiSelectChange}
                  isMulti
                  className={`basic-multi-select ${
                    errors.listOfLeads ? "is-invalid" : ""
                  }`}
                  classNamePrefix="select"
                  placeholder="Select leads to assign to group"
                  isLoading={isLoadingLeads}
                  noOptionsMessage={() => "No leads available"}
                />
                {errors.listOfLeads && (
                  <div className="text-danger mt-1 small">
                    {errors.listOfLeads}
                  </div>
                )}
                <div className="form-text">
                  Select multiple leads to assign to this group
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Action Buttons */}
        <div className="p-3">
          {activeTab === "home" ? (
            <>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Group"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleAssignmentSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Assign Lead"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateGroupCard;
